import React, { useCallback, useContext, useEffect, useId, useRef, useState } from "react";
import { UI } from "@wwimmo/ui";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { MessageType } from "src/components/notifications/Notifier";
import styles from "./CopyToClipboardButton.module.css";

const COPIED_ICON_DURATION_IN_MS = 2000;
const ICON_SIZE_IN_PX = 18;

interface ICopyToClipboardButtonProps {
    value: string;
    className?: string;
}

export const CopyToClipboardButton = (props: ICopyToClipboardButtonProps) => {
    const { uiStore } = useContext(RootStoreContext);
    const { t } = useTranslation();
    const tooltipId = useId();
    const [hasCopiedValue, setHasCopiedValue] = useState<boolean>(false);
    const copiedIconTimeout = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
        return () => clearTimeout(copiedIconTimeout.current);
    }, []);

    const copyValueToClipboard = useCallback(
        async (event: React.MouseEvent<HTMLElement>) => {
            // Der Button kann in klickbaren Listenzeilen stehen, welche beim Kopieren nicht reagieren dürfen
            event.preventDefault();
            event.stopPropagation();

            try {
                await navigator.clipboard.writeText(props.value);
            } catch {
                uiStore.printStatusMessage(t("labels.copy_to_clipboard_error"), MessageType.ERROR);
                return;
            }

            setHasCopiedValue(true);
            clearTimeout(copiedIconTimeout.current);
            copiedIconTimeout.current = setTimeout(() => setHasCopiedValue(false), COPIED_ICON_DURATION_IN_MS);
        },
        [props.value, t, uiStore]
    );

    return (
        <UI.OverlayTrigger
            placement="top"
            overlay={<UI.LightTooltip id={tooltipId}>{t("labels.copy_to_clipboard")}</UI.LightTooltip>}
        >
            <UI.BootstrapButton
                variant="link"
                className={`${styles.copyButton} ${props.className ?? ""}`}
                onClick={copyValueToClipboard}
            >
                <UI.Icon
                    icon={hasCopiedValue ? UI.SVGIcon.Check : UI.SVGIcon.Copy}
                    size={ICON_SIZE_IN_PX}
                    className={hasCopiedValue ? "text-success" : undefined}
                />
                <span className={styles.visuallyHidden}>{t("labels.copy_to_clipboard")}</span>
            </UI.BootstrapButton>
        </UI.OverlayTrigger>
    );
};
